import React, { useState } from 'react';
import './LoginPopup.css';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useFormValidation } from '../hooks/useFormValidation';
import { VALIDATION_RULES } from '../utils/validation';
import EmailVerificationOTP from './EmailVerificationOTP';
import apiService from '../services/apiService';

function LoginPopup({ onClose, onSignupClick }) {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [inactiveEmail, setInactiveEmail] = useState('');
  const [loginError, setLoginError] = useState('');

  // Initialize form validation
  const {
    formData,
    isValid,
    handleInputChange,
    handleFieldBlur,
    handleSubmit,
    getFieldValidationAttributes,
    hasError,
    getFieldError
  } = useFormValidation(
    { email: '', password: '' },
    {
      email: VALIDATION_RULES.email,
      password: VALIDATION_RULES.password
    },
    { validateOnBlur: true }
  );

  const onSubmit = async (data) => {
    setLoginError('');
    try {
      await login(data.email, data.password);
      onClose();
    } catch (error) {
      // Handle error from apiService (which returns error_code directly)
      const errorCode = error.error_code || error.response?.data?.error_code || error.response?.data?.error?.code;
      const errorMessage = error.message || error.response?.data?.detail || error.response?.data?.error?.message || 'Login failed. Please try again.';

      //console.log('Login error details:', { errorCode, errorMessage, error });

      if (errorCode === 'ACCOUNT_NOT_VERIFIED') {
        setInactiveEmail(data.email);
        setLoginError('Account not verified');
        // Don't auto-open OTP modal, show button instead
      } else {
        setLoginError(errorMessage);
        // Don't show toast here as apiService already shows it for non-account-not-verified errors
      }
      console.error('Login error:', error);
    }
  };

  const handleVerifyAccountClick = async () => {
    if (!inactiveEmail) return;

    try {
      // Send OTP to the unverified account email
      await apiService.post('/auth/send-email-verification-otp', {
        email: inactiveEmail,
      });

      toast.success('OTP sent to your email. Please check your inbox.');
      setShowOtpVerification(true);
      setLoginError('');
    } catch (error) {
      console.error('Failed to send OTP:', error);
      toast.error(error.message || 'Failed to send OTP. Please try again.');
    }
  };

  const handleVerificationComplete = () => {
    setShowOtpVerification(false);
    setInactiveEmail('');
    setLoginError('');
    toast.success('Email verified! You can now log in.');
  };

  const handleBackToLogin = () => {
    setShowOtpVerification(false);
    setInactiveEmail('');
    setLoginError('');
  };

  if (showOtpVerification) {
    return (
      <div className="popup-backdrop" onClick={onClose}>
        <EmailVerificationOTP
          email={inactiveEmail}
          onVerificationComplete={handleVerificationComplete}
          onBackToSignup={handleBackToLogin}
        />
      </div>
    );
  }

  return (
    <div className="popup-backdrop" onClick={onClose}>
      <div className="popup-content" onClick={e => e.stopPropagation()}>
        <button className="popup-close" onClick={onClose}>&times;</button>
        <h2 className="popup-title">Login</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="popup-form">
          {loginError && (
            <div className="login-error-banner">
              <p>{loginError}</p>
            </div>
          )}
          {loginError === 'Account not verified' && inactiveEmail && !showOtpVerification && (
            <div className="verification-prompt-banner">
              <p>Account not verified</p>
              <button
                type="button"
                className="verify-account-btn"
                onClick={handleVerifyAccountClick}
              >
                Verify the Account
              </button>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="login-email">Email</label>
            <input
              type="email"
              id="login-email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              placeholder="Enter your email"
              {...getFieldValidationAttributes('email')}
            />
            {hasError('email') && (
              <p className="error-message" id="email-error">
                {getFieldError('email')}
              </p>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <input
              type="password"
              id="login-password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              placeholder="Enter your password"
              {...getFieldValidationAttributes('password')}
            />
            {hasError('password') && (
              <p className="error-message" id="password-error">
                {getFieldError('password')}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="popup-submit"
            disabled={loading || !isValid || (loginError === 'Account not verified')}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <button
          onClick={onSignupClick}
          className="popup-switch-btn"
        >
          Don't have an account? Sign Up
        </button>
        <button
          onClick={() => { onClose(); navigate('/forgot-password'); }}
          className="popup-switch-btn"
          style={{ color: '#2196f3', background: 'none', border: 'none', marginTop: 8, cursor: 'pointer' }}
        >
          Forgot Password?
        </button>
      </div>
    </div>
  );
}

export default LoginPopup;